import React, { useEffect, useState } from 'react';
import {
  Text,
  Input,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Checkbox,
  Flex,
  SimpleGrid,
  Textarea,
  Box,
  Tooltip,
  Badge,
  useDisclosure,
} from '@chakra-ui/react';
import { toast } from 'react-toastify';
import httpInjectorService from 'services/http-injector.service';
import {
  Card,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import Spinner from 'components/common/Spinner';
import ExperienceLetter from './ExperienceLetter';
import { Empty } from 'antd';
import classnames from 'classnames';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FaEye } from 'react-icons/fa';
import { demoResignedUser } from 'components/demodata/Dummydata';

const OffBoarding = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [users, setUsers] = useState([]);
  const [status, setStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isModalOpen, setModalOpen] = useState(false);
  const [generateLetter, setGenerateLetter] = useState(false);
  const [preview, setPreview] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isSecondModalOpen, setSecondModalOpen] = useState(false);
  const [reasonForRejection, setReasonForRejection] = useState('');
  const [activeTab, setActiveTab] = useState('1');
  const [selectedItem, setSelectedItem] = useState(null);

  const handleView = (item) => {
    setSelectedItem(item);
    onOpen();
  };

  const formik = useFormik({
    initialValues: {
      lastWorkingDay: '',
    },
    validationSchema: Yup.object({
      lastWorkingDay: Yup.string().required('Last working day is required'),
    }),
    onSubmit: () => {
      handleModalClose();
      handleSubmit({
        ...currentUser,
        status,
        lastWorkingDay: formik.values.lastWorkingDay,
      });
    },
  });

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const getUsers = async () => {
    setIsLoading(true);
    try {
      const isDemoMode = localStorage.getItem('demoMode') === 'true';
                                    
        if (isDemoMode) {
          setUsers(demoResignedUser);
          return;
        }
      const response = await httpInjectorService.getResignedUsers();
      if (response.status === 'success') {
        // Map over the data and format resignation_timestamp and updated_at
        const formattedData = response.data.map((user) => ({
          ...user,
          resignation_timestamp: new Date(
            user.resignation_timestamp,
          ).toLocaleString([], {
            dateStyle: 'short', // Formats date as per local settings
            timeStyle: 'short', // Shows hour and minute
          }),
          updated_at: new Date(user.updated_at) // Add the same logic for updated_at
            .toLocaleString([], {
              dateStyle: 'short',
              timeStyle: 'short',
            }),
        }));
        setUsers(formattedData);
      } else {
        // toast.error(response.message, {
        //   position: 'top-right',
        //   autoClose: 3000,
        // });
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(e.target.value);
  };

  const filteredUsers = users.filter((user) => {
    const username = user.Name ? user.Name.toLowerCase() : '';
    const employeeId = user.Employee_id ? user.Employee_id.toString() : '';
    return (
      username.includes(searchQuery.toLowerCase()) ||
      employeeId.includes(searchQuery.toLowerCase())
    );
  });

  const openModal = (user) => {
    setCurrentUser(user);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    formik.resetForm();
    setGenerateLetter(false);
    setPreview(false);
  };

  const handleSecondModalClose = () => {
    setSecondModalOpen(false);
  };

  const handleSecondModalOpen = (user) => {
    setCurrentUser(user);
    setSecondModalOpen(true);
  };

  const handlePreview = () => {
    if (!formik.values.lastWorkingDay) {
      formik.setTouched({ lastWorkingDay: true });
      return;
    }
    setPreview(true);
  };

  const handleSubmit = async (data) => {
    try {
      const response = await httpInjectorService.offboardUsers(data);

      if (response.status === 'success') {
        toast.success(response.message, {
          position: 'top-right',
          autoClose: 3000,
        });

        handleModalClose(); // close modal after submission
        handleSecondModalClose();

        getUsers(); // REFRESH DATA WITHOUT PAGE RELOAD/REFRESH
      } else {
        toast.error(response.message, {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <>
      <Card
        className="shadow bg-white mb-4"
        style={{
          padding: '10px',
          marginTop: '60px',
          maxWidth: '100%',
          marginLeft: 'auto',
          marginRight: 'auto',
          borderRadius: '10px',
          border: '1px solid #E2E8F0',
          boxShadow: '0 18px 45px rgba(15, 23, 42, 0.12)',
        }}
      >
        <Nav tabs>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '1' })}
              onClick={() => toggleTab('1')}
              style={{ cursor: 'pointer', color: 'purple' }}
            >
              <span className="d-none d-sm-block">Offboarding</span>
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={activeTab}>
          <TabPane tabId="1">
            <div
              style={{ marginTop: '10px' }}
              className="card-header d-flex justify-content-between align-items-center"
            >
              <h5 className="mb-0">Offboarding</h5>
              <Flex align="center">
                <select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  style={{
                    width: '110px',
                    height: '35px',
                    borderRadius: '10px',
                    border: '1px solid #ccc',
                    marginRight: '10px',
                  }}
                >
                  <option value={10}>10 rows</option>
                  <option value={20}>20 rows</option>
                  <option value={50}>50 rows</option>
                </select>
                <Input
                  placeholder="Search by name or employee ID"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  width="270px"
                />
              </Flex>
            </div>
            {isLoading ? (
              <Spinner />
            ) : (
              <Box
                overflowX={{ base: 'auto', md: 'visible' }}
                overflowY="auto"
                width="100%"
                maxHeight="400px"
                style={{
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'thin',
                }}
                sx={{
                  '&::-webkit-scrollbar': { height: '8px', width: '8px' },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#888',
                    borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#555',
                  },
                }}
              >
                <Table bordered style={{ minWidth: '600px', width: '100%' }}>
                  <thead
                    backgroundColor="#B58EE4"
                    style={{
                      textAlign: 'center',
                      fontWeight: 'bold',
                      color: 'black',
                      position: 'sticky',
                      verticalAlign: 'middle',
                      // top: 0,
                      // zIndex: 1,
                    }}
                  >
                    <tr>
                      <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>
                        Name
                      </th>
                      <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>
                        Employee ID
                      </th>
                      <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>
                        Department
                      </th>
                      <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>
                        Designation
                      </th>
                      <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>
                        Timestamp
                      </th>
                      <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>
                        Last working day
                      </th>
                      <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    style={{ textAlign: 'center', verticalAlign: 'middle' }}
                  >
                    {filteredUsers.length > 0 ? (
                      filteredUsers.slice(0, rowsPerPage).map((user) => (
                        <tr
                          key={user.Employee_id}
                          borderColor="#c0c0c0"
                          borderWidth="1px"
                        >
                          <td borderColor="#c0c0c0" borderWidth="1px">
                            {user.Name}
                          </td>
                          <td borderColor="#c0c0c0" borderWidth="1px">
                            {user.Employee_id}
                          </td>
                          <td borderColor="#c0c0c0" borderWidth="1px">
                            {user.department_name}
                          </td>
                          <td borderColor="#c0c0c0" borderWidth="1px">
                            {user.designations}
                          </td>
                          <td borderColor="#c0c0c0" borderWidth="1px">
                            {user.resignation_timestamp}
                          </td>
                          <td>
                            {user.Last_working_day
                              ? new Date(
                                  user.Last_working_day,
                                ).toLocaleDateString()
                              : '-'}
                          </td>
                          <td
                            borderColor="#c0c0c0"
                            borderWidth="1px"
                            style={{ width: '160px' }}
                          >
                            {user.status === 'approved' ? (
                              <Badge
                                colorScheme="green"
                                px={2}
                                py={1}
                                borderRadius="md"
                              >
                                Approved
                              </Badge>
                            ) : user.status === 'rejected' ? (
                              <Badge
                                colorScheme="red"
                                px={2}
                                py={1}
                                borderRadius="md"
                              >
                                Rejected
                              </Badge>
                            ) : user.status === 'undo' ? (
                              <Badge
                                colorScheme="orange"
                                px={2}
                                py={1}
                                borderRadius="md"
                              >
                                Withdrawn
                              </Badge>
                            ) : (
                              <Flex gap={2} justify="center">
                                {/* ✅ APPROVE */}
                                <Tooltip label="Offboard Employee">
                                  <Button
                                    colorScheme="purple"
                                    borderRadius="sm"
                                    size="xs"
                                    onClick={() => {
                                      openModal(user);
                                      setStatus('approved');
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faCheck} />
                                  </Button>
                                </Tooltip>

                                {/* ❌ REJECT */}
                                <Tooltip label="Reject Resignation">
                                  <Button
                                    colorScheme="red"
                                    borderRadius="sm"
                                    size="xs"
                                    onClick={() => {
                                      handleSecondModalOpen(user);
                                      setStatus('rejected');
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faTimes} />
                                  </Button>
                                </Tooltip>

                                <Tooltip
                                  label="View Resignation Details"
                                  hasArrow
                                >
                                  <Button
                                    size="xs"
                                    borderRadius="sm"
                                    colorScheme="blue"
                                    onClick={() => handleView(user)}
                                  >
                                    <FaEye />
                                  </Button>
                                </Tooltip>
                              </Flex>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center' }}>
                          <Empty
                            description="No users found"
                            style={{ fontSize: '24px' }}
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Box>
            )}
          </TabPane>
        </TabContent>
      </Card>

      {/* view resignation details modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Resignation Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              <b>Name:</b> {selectedItem?.Name}
            </Text>
            <Text>
              <b>Employee ID:</b> {selectedItem?.Employee_id}
            </Text>
            <Text>
              <b>Email:</b> {selectedItem?.personalemail_id}
            </Text>
            <Text>
              <b>Mobile:</b> {selectedItem?.mobile_number}
            </Text>
            <Text>
              <b>Address:</b> {selectedItem?.address}
            </Text>
            <Text>
              <b>Reason:</b>{' '}
              {selectedItem?.reason_for_resignation
                ? selectedItem.reason_for_resignation
                : '-'}
            </Text>

            <Text mt={2}>
              <b>Status:</b>{' '}
              {selectedItem?.status ? selectedItem.status : 'Pending'}
            </Text>

            <Text>
              <Text>
                <b>Applied On:</b> {selectedItem?.resignation_timestamp || '-'}
              </Text>
            </Text>

            <Text>
              <b>Last Working Day:</b>{' '}
              {selectedItem?.Last_working_day
                ? new Date(selectedItem.Last_working_day).toLocaleDateString()
                : '-'}
            </Text>
          </ModalBody>

          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Offboarding Modal */}
      <Modal isOpen={isModalOpen} onClose={handleModalClose} size="3xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Employee Offboarding</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={2} spacing={4} mb={4}>
              <Input
                placeholder="Additional Field 1"
                value={currentUser?.Name || ''}
                isDisabled
                style={{ fontWeight: 'bold', color: '#000' }}
              />
              <Input
                placeholder="Additional Field 2"
                value={currentUser?.Employee_id || ''}
                isDisabled
                style={{ fontWeight: 'bold', color: '#000' }}
              />
            </SimpleGrid>
            <SimpleGrid columns={2} spacing={4} mb={4}>
              <Input
                placeholder="Additional Field 3"
                value={currentUser?.department_name || ''}
                isDisabled
                style={{ fontWeight: 'bold', color: '#000' }}
              />
              <Input
                placeholder="Additional Field 4"
                value={currentUser?.designations || ''}
                isDisabled
                style={{ fontWeight: 'bold', color: '#000' }}
              />
            </SimpleGrid>
            <SimpleGrid columns={2} spacing={4} mb={4}>
              <Input
                placeholder="Personal Email ID"
                value={currentUser?.personalemail_id || ''}
                isDisabled
                style={{ fontWeight: 'bold', color: '#000' }}
              />
              <Input
                placeholder="Phone No"
                value={currentUser?.mobile_number || ''}
                isDisabled
                style={{ fontWeight: 'bold', color: '#000' }}
              />
            </SimpleGrid>
            <SimpleGrid columns={2} spacing={4} mb={4}>
              <Input
                placeholder="Emergency Contact"
                value={currentUser?.emergency_contact_number || ''}
                isDisabled
                style={{ fontWeight: 'bold', color: '#000' }}
              />
              <Input
                placeholder="Address"
                value={currentUser?.address || ''}
                isDisabled
                style={{ fontWeight: 'bold', color: '#000' }}
              />
            </SimpleGrid>
            <label htmlFor="lastWorkingDay">Last Working Day:</label>
            <Input
              type="date"
              name="lastWorkingDay"
              value={formik.values.lastWorkingDay}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              mb={1}
            />

            {formik.touched.lastWorkingDay && formik.errors.lastWorkingDay && (
              <Box color="red.500" fontSize="sm" mb={2}>
                {formik.errors.lastWorkingDay}
              </Box>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Checkbox
                isChecked={generateLetter}
                onChange={(e) => setGenerateLetter(e.target.checked)}
                mb={4}
              >
                Generate Relieving Letter
              </Checkbox>
              {generateLetter && (
                <Button onClick={handlePreview} colorScheme="teal" mt={2}>
                  Preview
                </Button>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="purple" onClick={formik.handleSubmit}>
              Submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isSecondModalOpen}
        onClose={handleSecondModalClose}
        size="3xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reject Resignation</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={2} spacing={4} mb={4}>
              <Input
                placeholder="Additional Field 1"
                value={currentUser?.Name || ''}
                isDisabled
                style={{ fontWeight: 'bold', color: '#000' }}
              />
              <Input
                placeholder="Additional Field 2"
                value={currentUser?.Employee_id || ''}
                isDisabled
                style={{ fontWeight: 'bold', color: '#000' }}
              />
            </SimpleGrid>
            <SimpleGrid columns={2} spacing={4} mb={4}>
              <Input
                placeholder="Additional Field 3"
                value={currentUser?.department_name || ''}
                isDisabled
                style={{ fontWeight: 'bold', color: '#000' }}
              />
              <Input
                placeholder="Additional Field 4"
                value={currentUser?.designations || ''}
                isDisabled
                style={{ fontWeight: 'bold', color: '#000' }}
              />
            </SimpleGrid>
            <SimpleGrid columns={2} spacing={4} mb={4}>
              <Input
                placeholder="Personal Email ID"
                value={currentUser?.personalemail_id || ''}
                isDisabled
                style={{ fontWeight: 'bold', color: '#000' }}
              />
              <Input
                placeholder="Phone No"
                value={currentUser?.mobile_number || ''}
                isDisabled
                style={{ fontWeight: 'bold', color: '#000' }}
              />
            </SimpleGrid>
            <SimpleGrid columns={2} spacing={4} mb={4}>
              <Input
                placeholder="Emergency Contact"
                value={currentUser?.emergency_contact_number || ''}
                isDisabled
                style={{ fontWeight: 'bold', color: '#000' }}
              />
              <Input
                placeholder="Address"
                value={currentUser?.address || ''}
                isDisabled
                style={{ fontWeight: 'bold', color: '#000' }}
              />
            </SimpleGrid>
            <label htmlFor="Reason for rejection">Reason for rejection</label>
            <Textarea
              type="date"
              id="Reason for rejection"
              value={reasonForRejection}
              onChange={(e) => {
                setReasonForRejection(e.target.value);
              }}
              placeholder="Reason for rejection"
              mb={3}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="purple"
              onClick={() => {
                handleSecondModalClose();
                handleSubmit({ ...currentUser, status, reasonForRejection });
              }}
            >
              Submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Experience Letter Preview Modal */}
      <Modal isOpen={preview} onClose={() => setPreview(false)} size="lg">
        <ModalOverlay />
        <ModalContent
          style={{
            width: '80%', // Adjust the width as needed
            maxWidth: '800px', // Set a maximum width
            top: '1%', // Position it closer to the top
            margin: '0 auto', // Center it horizontally
          }}
        >
          <ModalHeader>Experience Letter Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {currentUser && (
              <ExperienceLetter
                name={currentUser.Name}
                employeeId={currentUser.Employee_id}
                department={currentUser.department_name}
                designation={currentUser.designations}
                lastWorkingDay={formik.values.lastWorkingDay}
                dateOfJoining={currentUser.DateofJoining}
                Address={currentUser.Address}
                companyName={currentUser.org_name}
                logo={currentUser.logo_url}
              />
            )}
          </ModalBody>
          <ModalFooter></ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default OffBoarding;
